import {
  addQuestionToSurvey as apiAddQuestionToSurvey,
  removeQuestionFromSurvey as apiRemoveQuestionFromSurvey,
  updateFeedbackQuestion as apiUpdateFeedbackQuestion,
  createFeedbackQuestion,
  createSurvey,
  deleteFeedbackQuestion,
  deleteSurvey,
  updateSurveyQuestionOrder,
} from '@lms/api-client/backend';
import { supportedLanguages } from '@/features/locale/supported-languages';
import type { QuestionInput } from './actions.schema';

// Ports features/survey/actions.ts. Each Next server action becomes a plain
// async function that calls the backend and returns a result the caller acts
// on. `revalidatePath`/`redirect` are dropped; callers revalidate their SWR
// cache with `mutate` and navigate with `useNavigate` after a successful call.
// `answerSurvey` is intentionally not ported here — the trainee-facing survey
// form lives in `features/scormplayer`.

export type MutationResult = { ok: true } | { ok: false; error: string };

// Ports the per-locale record building from the Next `addQuestion` /
// `updateQuestion` actions: only locales with a non-empty value are sent.
function buildTranslations(input: QuestionInput): {
  beforeQuestion: Record<string, string>;
  afterQuestion: Record<string, string>;
} {
  const beforeQuestion: Record<string, string> = {};
  const afterQuestion: Record<string, string> = {};
  for (const locale of supportedLanguages) {
    const before = input.beforeQuestion[locale];
    if (before) {
      beforeQuestion[locale] = before;
    }
    const after = input.afterQuestion[locale];
    if (after) {
      afterQuestion[locale] = after;
    }
  }
  return { beforeQuestion, afterQuestion };
}

// Ports `addSurvey`.
export async function addSurvey(name: string): Promise<MutationResult> {
  try {
    await createSurvey({ name });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `removeSurvey`.
export async function removeSurvey(id: number): Promise<MutationResult> {
  try {
    await deleteSurvey(id);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `addQuestion`.
export async function addQuestion(
  input: QuestionInput,
): Promise<MutationResult> {
  try {
    const { beforeQuestion, afterQuestion } = buildTranslations(input);
    await createFeedbackQuestion({
      description: input.description,
      beforeQuestion,
      afterQuestion,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `updateQuestion`.
export async function updateQuestion(
  id: number,
  input: QuestionInput,
): Promise<MutationResult> {
  try {
    const { beforeQuestion, afterQuestion } = buildTranslations(input);
    await apiUpdateFeedbackQuestion(id, {
      description: input.description,
      beforeQuestion,
      afterQuestion,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `removeQuestion`.
export async function removeQuestion(id: number): Promise<MutationResult> {
  try {
    await deleteFeedbackQuestion(id);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `addQuestionToSurveyAction`.
export async function addQuestionToSurveyAction(
  surveyId: number,
  questionId: number,
): Promise<MutationResult> {
  try {
    await apiAddQuestionToSurvey(surveyId, questionId);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `removeQuestionFromSurveyAction`.
export async function removeQuestionFromSurveyAction(
  surveyId: number,
  questionId: number,
): Promise<MutationResult> {
  try {
    await apiRemoveQuestionFromSurvey(surveyId, questionId);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `updateQuestionOrderInSurvey`. `order` is a relative move (-1 up, +1
// down); the Next action bailed out on anything outside that range, so we keep
// the same guard before hitting the backend's atomic swap.
export async function updateQuestionOrderInSurvey(
  surveyId: number,
  questionId: number,
  order: number,
): Promise<MutationResult> {
  if (order < -1 || order > 1) {
    return { ok: true };
  }
  try {
    await updateSurveyQuestionOrder(surveyId, questionId, order);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}
